import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { ReactNode } from "react";
import { getAccessToken } from "../utils/msalConfig";

const placeholderCustomer = {
  oid: "",
  email: "",
  role: "",
};

interface IdTokenClaims {
  oid?: string;
  [key: string]: any;
}

interface User {
  oid: string;
  email: string;
  role: string;
}

export function getEntraUserDetails(idTokenClaims: IdTokenClaims): User {
  return {
    oid: idTokenClaims?.oid || "",
    email: "",
    role: "",
  };
}

export const AppContext = createContext({
  user: placeholderCustomer,
});

export const useAppContext = () => {
  return useContext(AppContext);
};

interface AppProviderProps {
  children: ReactNode;
}

export default function AppProvider({ children }: AppProviderProps) {
  const [user, setUser] = useState(placeholderCustomer);
  const { accounts, instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const getToken = async () => {
      try {
        if (!accounts || accounts.length === 0) {
          console.error("No accounts found");
          return;
        }

        const token = await getAccessToken(accounts[0]);
        const claims = accounts[0]?.idTokenClaims;
        localStorage.setItem("access_token", token || "");
  
        if (claims) {
          setUser(getEntraUserDetails(claims));
        }
      } catch (e) {
        console.error("Error acquiring token:", e);
        if ((e as any)?.errorMessage?.includes("AADB2C90077")) {
          console.log("Session expired. Logging out...");
          localStorage.clear();
          try {
            await instance.logoutRedirect();
          } catch (err) {
            console.log("Logout failure! Please try again ...");
          }
        }
      }
    };
  
    if (inProgress === InteractionStatus.None && isAuthenticated) {
      getToken();
    }
  }, [accounts, instance, inProgress, isAuthenticated]);
  


  const context = useMemo(
    () => ({
      user,
    }),
    [user]
  );

  return <AppContext.Provider value={context}>{children}</AppContext.Provider>;
}
